import { translate } from "shared/languages";
import { Notification } from "shared/types";
import { translateMessage } from "shared/utils";
import { localDateOnly } from "shared/utils/dayjs";

// The server writes dates into notification text: "15 Okt 2026" in the
// package alerts (notification.ts) and "Senin, 12 Okt 2026" in the clash
// digest (schedule_v2.ts, both its HTML and its payload.date). Stored text
// cannot follow the admin's language, so every date of exactly that shape is
// formatted again in the active language here. The server writes Indonesian,
// except on the nights of 27 and 28 Sep 2026 (its Indonesian locale was not
// loaded then), so English names are read too: in Indonesian those rows then
// say "05 Okt 2026" instead of "05 Oct 2026". A date already in the active
// language comes out as the same string, and anything that is not that shape
// (a booking row's "15 Sep 17:54", which has no year) is left alone.
const MONTHS: string[][] = [
  ["Jan"], ["Feb"], ["Mar"], ["Apr"], ["Mei", "May"], ["Jun"],
  ["Jul"], ["Agt", "Aug"], ["Sep"], ["Okt", "Oct"], ["Nov"], ["Des", "Dec"],
];
const WEEKDAYS = [
  "Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu",
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];
const SERVER_DATE = new RegExp(
  `(?:(${WEEKDAYS.join("|")}), )?(\\d{1,2}) (${MONTHS.flat().join("|")}) (\\d{4})`,
  "g",
);

export const localizeServerDates = (text: string): string =>
  text.replace(SERVER_DATE, (match, weekday: string | undefined, day: string, month: string, year: string) => {
    const monthIndex = MONTHS.findIndex((names) => names.includes(month));
    const iso = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${day.padStart(2, "0")}`;
    // Date-only, so it goes through localDateOnly: tzDate would read the bare
    // date in the browser's zone and land a day early east of Jakarta.
    const date = localDateOnly(iso);
    if (!date.isValid()) return match;
    return date.format(weekday ? "dddd, DD MMM YYYY" : "DD MMM YYYY");
  });

// A claimed reward's stored body is "<b>N</b> translate('notifications.loyalty.claimed_credits')"
// (emitRewardClaimNotification). The sentence is picked by N here, so English
// reads "1 reward credit" / "2 reward credits" instead of "credit(s)". A row in
// any other shape keeps its stored key.
const REWARD_CLAIM = /<b>(\d+)<\/b> translate\('notifications\.loyalty\.claimed_credits'\)/;

// The HTML body of a notification without a structured layout (package
// alerts, reward claims, legacy clash digests), ready for the rail and the
// drawer: translate() tokens resolved, the reward count's sentence picked,
// and the server's Indonesian dates in the active language.
export const notificationContentHtml = (notification: Notification): string => {
  const content = (notification.content ?? "").replace(
    REWARD_CLAIM,
    (_, n: string) =>
      `<b>${n}</b> ${translate(
        Number(n) === 1
          ? "notifications.loyalty.claimed_credits_single"
          : "notifications.loyalty.claimed_credits_many",
      )}`,
  );
  return localizeServerDates(translateMessage(content));
};
